import React from "react";
import { cn } from "@/lib/utils";

// Khan-Academy-Kids tactile button.
// Variants now use the softer pastel palette wired in tailwind.config.js
// (yellow ≈ #FFD166, sky ≈ #48A9A6, green ≈ #90BE6D, red/rose ≈ #FF8E72,
// pink ≈ #F4978E, orange ≈ #F4A261, white = cream, navy = slate-blue #1D3557).
//
// Shadows are now soft multi-layer drop shadows (small idle → larger on hover)
// instead of the brutalist hard offsets. The 2px border + ring-style focus
// keeps a11y intact without feeling harsh.
const VARIANTS = {
  yellow: "bg-yellow-300 text-slate-900 border-yellow-400 hover:bg-yellow-400",
  sky:    "bg-sky-400 text-white border-sky-400 hover:bg-sky-500",
  green:  "bg-emerald-400 text-white border-emerald-400 hover:bg-emerald-500",
  red:    "bg-rose-400 text-slate-900 border-rose-400 hover:bg-rose-500",
  pink:   "bg-pink-400 text-slate-900 border-pink-400 hover:bg-pink-500",
  orange: "bg-orange-400 text-slate-900 border-orange-400 hover:bg-orange-500",
  white:  "bg-white text-slate-900 border-slate-200 hover:bg-slate-50",
  navy:   "bg-slate-900 text-white border-slate-900 hover:bg-slate-800",
};

// Sizes: heights pinned in absolute px so the 15% root-font-shrink (app-density
// mode) doesn't push them below the touch-target floor. sm/md/lg cover the
// common WCAG 2.5.5 thresholds for kid-safe tap zones.
const SIZES = {
  sm: "min-h-[40px] px-3 text-base shadow-[0_2px_8px_rgba(29,53,87,0.06)] hover:shadow-[0_4px_14px_rgba(29,53,87,0.1)] active:scale-[0.98] border-2",
  md: "min-h-[44px] px-5 text-lg shadow-[0_4px_14px_rgba(29,53,87,0.08)] hover:shadow-[0_6px_18px_rgba(29,53,87,0.12)] active:scale-[0.98] border-2",
  lg: "min-h-[52px] px-7 text-xl md:text-2xl shadow-[0_6px_18px_rgba(29,53,87,0.1)] hover:shadow-[0_10px_28px_rgba(29,53,87,0.14)] active:scale-[0.98] border-2",
};

export default function KidButton({
  children,
  variant = "yellow",
  size = "md",
  className,
  ...props
}) {
  return (
    <button
      className={cn(
        "rounded-full font-bold tracking-wide",
        "transition-all duration-150 ease-out hover:-translate-y-[1px] active:translate-y-0",
        "focus:outline-none focus-visible:ring-4 focus-visible:ring-sky-200 focus-visible:ring-offset-2",
        "disabled:opacity-60 disabled:pointer-events-none disabled:hover:translate-y-0",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
